import React, { useState, useEffect, ChangeEvent, FC } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import CloseSelect from '../../../assets/select.svg'
import OpenSelect from '../../../assets/selectOpen.svg'
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";

interface Option {
    value: string | number;
    label: string;
    [key: string]: any;
}

interface CustomDropdownProps {
    options: Option[];
    placeholder?: string;
    value?: Option | null;
    onChange: (option: Option) => void;
    name?: string;
    itemTemplate?: (option: Option) => React.ReactNode;
    addLabelHandler?: () => void;
    searchPlaceholder?: string;
    addButtonLabel?: string;
    setSelectedlabel: (selectedlabel: string) => void
    onHide: () => void;
    values: any
    setCreate:(create:string) => void

}

const CustomDropdown: FC<CustomDropdownProps> = ({
    options,
    placeholder = IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.ChooseLabel",
        defaultMessage: "Choose a label"
    }),
    value = null,
    itemTemplate,
    addLabelHandler,
    searchPlaceholder = IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.SearchPlaceholder",
        defaultMessage: "Enter label name to search"
    }),
    addButtonLabel = IntlStore.intl.formatMessage({
        id: "NewRelease.ReleaseInfo.AddLabel",
        defaultMessage: "Add Label"
    }),
    setSelectedlabel,
    onHide,
    values,
    setCreate
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const [labelValue, setLabelValue] = useState('')
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!search) {
            setFilteredOptions(options);
        } else {
            const lowerSearch = search.toLowerCase();
            setFilteredOptions(
                options.filter((opt) =>
                    String(opt.label).toLowerCase().includes(lowerSearch)
                )
            );
        }
    }, [search, options]);

    useEffect(() => {
        if (open && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    }, [open]);

    const list = (
        <OptionsList
            role="listbox"
            top={position.top}
            left={position.left}
            width={position.width}
        >
            <SearchInput
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
            />
            <OptionItemBox>
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                        <OptionItem
                            style={{
                                backgroundColor: labelValue == option.value ? "#E2E5FF" : "transparent",
                            }}
                            key={index}
                            onClick={() => {
                                const found = filteredOptions.find(o => o.value === option.value);

                                if (found) {
                                    setLabelValue(String(found.value));
                                }

                              
                                    values.label =  String(option.value)
                                setSelectedlabel('')
                                setOpen(false);
        
                                if (addLabelHandler) addLabelHandler();
                                onHide()
                            }} 
                            
                            selected={labelValue === String(option.id)}
                        >
                            {itemTemplate ? itemTemplate(option) : option.label}
                        </OptionItem>
                    ))
                ) : (
                    <div>
                        <FormattedMessage
                          id="NewRelease.ReleaseInfo.NoResults"
                          defaultMessage="No results"
                        />
                    </div>
                )}
            </OptionItemBox>
            <AddButtonBox>
                <AddButton
                    type="button"
                    onClick={() => {
                        setCreate('Create')
                    }}
                >
                    {addButtonLabel}
                </AddButton>
            </AddButtonBox>
        </OptionsList>
    );

    return (
        <DropdownContainer ref={dropdownRef}>
            <SelectedBox onClick={() => setOpen((o) => !o)}>
                {value ? value.label : placeholder}
                <Arrow>{open ? <OpenSelect /> : <CloseSelect />}</Arrow>
            </SelectedBox>
            <div>
                {open && createPortal(list, document.body)}
            </div>
        </DropdownContainer>
    );
};

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  display:flex;
  flex-direction: column;
  gap:5px;
`
const SelectedBox = styled.div`
  border: 1px solid #ccc;
  padding: 9px 16px;
  cursor: pointer;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
`
const Arrow = styled.span`
  font-size: 14px;
`
const OptionsList = styled.div<{ top: number; left: number; width: number }>`
  width:100%;
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  max-height: 294px;
  border: 1px solid #ccc;
  background-color: #fff;
  border-radius: 8px;
  padding: 12px 16px 16px 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`
const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;

  /* iOS Safari zooms the page if focused input font-size < 16px */
  font-size: 16px;
  line-height: 1.4;
`
const OptionItemBox = styled.div`
  overflow-y: auto;
  width:100%;
  height:173px;
`
const OptionItem = styled.div<{ selected?: boolean }>`
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  background-color: ${({ selected }) => (selected ? '#D1DBFF' : 'transparent')};
  font-weight: ${({ selected }) => (selected ? '600' : '400')};

  &:hover {
    background-color: #e6e9ff;
  }
`
const AddButton = styled.button`
  
  height: 35px;
  border-radius: 8px;
  margin-top: auto;
  padding: 12px;
  background-color: #6777FF;
  color: white;
  border: none;
  cursor: pointer;
`
const AddButtonBox = styled.div`
  width:100%;
  height:67px;
  border-top:1px solid #00000033;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export default CustomDropdown;
